import { useState, useEffect } from 'react';
import { useLang } from '../../config/i18n';
import { buildTokenSignUrl, buildWaUrl, buildSignMessage, buildSignedPdfHtml, buildPaidReceiptMessage } from '../../lib/invoiceHelpers';
import CustomerRegistrationTab from './Register';

export default function InvoicesTab({ tenants = [], units = [], contracts = [], invoices = [], addInvoice = ()=>{}, updateInvoice = ()=>{}, deleteInvoice = ()=>{}, clearSignature = ()=>{}, sbConfig = null, signatures = {}, refreshSignatures = ()=>{}, setMonthly = ()=>{} }){
  const [sub, setSub] = useState("new"); // "new" | "archive"

  const subBtn = (key, label, count) => {
    const active = sub === key;
    return (
      <button onClick={()=>setSub(key)} style={{
        padding:"10px 18px",
        background: active ? "rgba(200,160,60,0.15)" : "rgba(255,255,255,0.03)",
        border: `1px solid ${active ? "rgba(200,160,60,0.45)" : "rgba(255,255,255,0.06)"}`,
        color: active ? "#c8a03c" : "#94a3b8",
        borderRadius: 10,
        fontSize: 13,
        fontWeight: 800,
        cursor: "pointer",
        fontFamily: "'Tajawal',sans-serif",
        display: "flex",
        alignItems: "center",
        gap: 6
      }}>
        {label}
        {count != null && <span style={{
          background: active ? "#c8a03c" : "rgba(255,255,255,0.08)",
          color: active ? "#0d0f1a" : "#94a3b8",
          padding: "1px 8px",
          borderRadius: 10,
          fontSize: 11
        }}>{count}</span>}
      </button>
    );
  };

  return (
    <div>
      {/* Sub-tab strip */}
      <div style={{padding:"18px 28px 0",maxWidth:1280,margin:"0 auto",display:"flex",gap:10,flexWrap:"wrap"}}>
        {subBtn("new",     "+ فاتورة جديدة")}
        {subBtn("archive", "📜 الأرشيف", invoices.length)}
      </div>
      {sub === "new"
        ? <CustomerRegistrationTab tenants={tenants} units={units} contracts={contracts} invoices={invoices} addInvoice={addInvoice} sbConfig={sbConfig}/>
        : <InvoicesArchiveTab invoices={invoices} units={units} contracts={contracts} signatures={signatures} refreshSignatures={refreshSignatures} updateInvoice={updateInvoice} deleteInvoice={deleteInvoice} clearSignature={clearSignature} sbConfig={sbConfig} setMonthly={setMonthly}/>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Invoices Archive Tab — list of past invoices with date, unit, totals
// ══════════════════════════════════════════════════════════════════
// ── Inline hashPin (matches App.js) ──────────────────────────────────────────
async function hashPin(pin){
  const data = new TextEncoder().encode(pin + "findre-sa-2026");
  const buf  = await window.crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(buf)).map(b=>b.toString(16).padStart(2,"0")).join("");
}

function InvoicesArchiveTab({ invoices = [], units = [], contracts = [], signatures = {}, refreshSignatures = ()=>{}, updateInvoice = ()=>{}, deleteInvoice = ()=>{}, clearSignature = ()=>{}, sbConfig = null, setMonthly = ()=>{} }){
  const {S, lang, dir, font} = useLang();
  const T = S.invoices;
  const [search,   setSearch]   = useState("");
  const [viewSig,  setViewSig]  = useState(null);
  const [editing,  setEditing]  = useState(null);
  const [editForm, setEditForm] = useState({});

  // ── PIN-protected delete ──────────────────────────────────────────
  const [deleteTarget,   setDeleteTarget]   = useState(null); // invoice pending delete
  const [delPin,         setDelPin]         = useState("");
  const [delPinError,    setDelPinError]    = useState(false);
  const [delPinAttempts, setDelPinAttempts] = useState(0);
  const [delPinLocked,   setDelPinLocked]   = useState(false);
  const [delLockUntil,   setDelLockUntil]   = useState(null);

  function openDeleteModal(inv){ setDeleteTarget(inv); setDelPin(""); setDelPinError(false); }
  function closeDeleteModal(){ setDeleteTarget(null); setDelPin(""); setDelPinError(false); }

  async function submitDeletePin(pin){
    const lockKey = "findre-delete-pin-lockout";
    const lockData = JSON.parse(localStorage.getItem(lockKey)||"{}");
    if(lockData.lockedUntil && Date.now() < lockData.lockedUntil){
      setDelPinLocked(true); setDelLockUntil(lockData.lockedUntil); setDelPin(""); return;
    }
    // Use same PIN hash as app (findre-pin-hash), fallback default 1234
    const storedHash = localStorage.getItem("findre-pin-hash") || await hashPin("1234");
    const inputHash  = await hashPin(pin);
    if(inputHash === storedHash){
      localStorage.removeItem(lockKey);
      setDelPinAttempts(0); setDelPinLocked(false);
      deleteInvoice(deleteTarget.id);
      closeDeleteModal();
    } else {
      const next = (lockData.attempts||0) + 1;
      const newLock = next >= 5
        ? { attempts:next, lockedUntil: Date.now() + 5*60*1000 }
        : { attempts:next };
      localStorage.setItem(lockKey, JSON.stringify(newLock));
      setDelPinAttempts(next);
      if(next >= 5){ setDelPinLocked(true); setDelLockUntil(newLock.lockedUntil); }
      setDelPinError(true); setDelPin("");
    }
  }

  // Auto-pull signatures from Supabase when archive opens
  useEffect(()=>{ refreshSignatures(); }, []); // eslint-disable-line

  async function resendLink(inv){
    const sigUrl = await buildTokenSignUrl({ ...inv, fullName: inv.customerName, phone: inv.customerPhone }, sbConfig);
    if(!sigUrl){ alert('تعذّر إنشاء رابط التوقيع — حاول مرة أخرى بعد قليل.'); return; }
    const msg = buildSignMessage(inv.customerName||"", inv.invoiceNo, sigUrl);
    window.open(buildWaUrl(inv.customerPhone, msg), '_blank');
  }
  function openEdit(inv){
    setEditForm({
      customerName: inv.customerName||"",
      customerPhone: inv.customerPhone||"",
      idNumber: inv.idNumber||"",
      unit: inv.unit||"",
      district: inv.district||"",
      city: inv.city||"الرياض",
      rent: inv.rent||0,
      deposit: inv.deposit||0,
      paymentMethod: inv.paymentMethod||"",
      contractStart: inv.contractStart||"",
      contractEnd: inv.contractEnd||"",
      lateDailyRate: inv.lateDailyRate||100,
    });
    setEditing(inv);
  }
  function saveEdit(){
    const rent = Number(editForm.rent)||0;
    const deposit = Number(editForm.deposit)||0;
    updateInvoice(editing.id, { ...editForm, rent, deposit, total: rent+deposit, lateDailyRate: Number(editForm.lateDailyRate)||100 });
    // Clear old signature — contract details changed, customer must re-sign
    clearSignature(editing.invoiceNo);
    setEditing(null);
  }
  function markInvoicePaid(inv){
    if(inv.paidAt){ alert("هذه الفاتورة مسجلة كمدفوعة مسبقاً."); return; }
    const today = new Date().toISOString().slice(0,10);
    updateInvoice(inv.id, { paidAt: today });
    // Update Monthly Ops — revenue recorded on payment date (cash basis)
    // Use contract.id as stable key (contracts are source of truth)
    const matchContract = (contracts||[]).find(c =>
      c.unitNumber === inv.unit || c.unitName === inv.unit
    );
    const monthlyUnitId = matchContract ? matchContract.id : `${inv.unit}-${inv.district}`;
    const mk = today.slice(0,7); // YYYY-MM
    setMonthly(prev => {
      const key = `${monthlyUnitId}-${mk}`;
      const existing = prev[key] || {};
      const rent = Number(inv.rent)||0;
      const customer = inv.customerName||'';
      // Build/extend the tenants[] list rather than overwriting — supports multi-tenant months.
      const existingTenants = Array.isArray(existing.tenants) ? existing.tenants : [];
      // Skip if this invoice already produced a row (match by customer+amount).
      const alreadyThere = existingTenants.some(t => (t.name||'').trim() === customer.trim() && Number(t.amount) === rent);
      const nextTenants = alreadyThere ? existingTenants : [
        ...existingTenants,
        ...(existingTenants.length === 0 && (existing.tenantName || existing.rentCollected)
          ? [{ id: 'legacy-'+Date.now(), name: existing.tenantName||'', amount: Number(existing.rentCollected)||0 }]
          : []),
        { id: 'inv-'+inv.id, name: customer, amount: rent }
      ];
      const totalRent = nextTenants.reduce((s,t)=>s+(Number(t.amount)||0),0);
      const nameJoined = nextTenants.map(t=>t.name).filter(Boolean).join(' · ');
      return { ...prev, [key]: {
        ...existing,
        status:'مشغول',
        tenants: nextTenants,
        tenantName: nameJoined,
        rentCollected: totalRent,
      }};
    });
  }
  function unmarkInvoicePaid(inv){
    if(!window.confirm(`هل تريد إلغاء تسجيل الدفع للفاتورة ${inv.invoiceNo}؟\nسيتم حذف تاريخ الدفع ومسح الإيراد من السجل الشهري.`)) return;
    updateInvoice(inv.id, { paidAt: null });
    // Revert monthly entry — clear rentCollected and reset status to شاغرة
    const matchContract = (contracts||[]).find(c =>
      c.unitNumber === inv.unit || c.unitName === inv.unit
    );
    const monthlyUnitId = matchContract ? matchContract.id : `${inv.unit}-${inv.district}`;
    const mk = (inv.paidAt||"").slice(0,7);
    if(mk){
      setMonthly(prev => {
        const key = `${monthlyUnitId}-${mk}`;
        if(!prev[key]) return prev;
        const existing = prev[key];
        // Remove ONLY the tenant row that came from this invoice; leave other tenants intact.
        const invRowId = 'inv-'+inv.id;
        const nextTenants = Array.isArray(existing.tenants) ? existing.tenants.filter(t=>t.id!==invRowId) : [];
        const totalRent = nextTenants.reduce((s,t)=>s+(Number(t.amount)||0),0);
        const nameJoined = nextTenants.map(t=>t.name).filter(Boolean).join(' · ');
        const updated = { ...existing, tenants: nextTenants, tenantName: nameJoined, rentCollected: totalRent };
        if(nextTenants.length === 0) updated.status = 'شاغرة';
        return { ...prev, [key]: updated };
      });
    }
  }

  function markVacated(inv){
    if(inv.vacatedAt){ alert("هذه الفاتورة مسجّل لها إخلاء بالفعل."); return; }
    const today = new Date().toISOString().slice(0,10);
    const date = window.prompt(
      `تاريخ الإخلاء الفعلي للوحدة ${inv.unit} — ${inv.customerName||""} (YYYY-MM-DD)`,
      today
    );
    if(!date) return;
    if(!/^\d{4}-\d{2}-\d{2}$/.test(date)){ alert("تاريخ غير صحيح. استخدم الصيغة YYYY-MM-DD"); return; }
    updateInvoice(inv.id, { vacatedAt: date });
  }
  function unmarkVacated(inv){
    if(!window.confirm(`إلغاء إخلاء الوحدة ${inv.unit}؟ ستعود الوحدة كأنها مؤجرة حتى ${inv.contractEnd}.`)) return;
    updateInvoice(inv.id, { vacatedAt: null });
  }

  const sorted = [...(invoices||[])].sort((a,b)=>(b.seq||0)-(a.seq||0));
  const q = search.trim().toLowerCase();
  const filtered = q
    ? sorted.filter(i =>
        (i.invoiceNo||"").toLowerCase().includes(q) ||
        (i.customerName||"").toLowerCase().includes(q) ||
        (i.unit||"").toString().toLowerCase().includes(q) ||
        (i.district||"").toLowerCase().includes(q)
      )
    : sorted;

  // إجمالي القيمة: rent only — deposit is refundable, not income.
  const totalAll = filtered.reduce((s,i)=>s+(Number(i.rent)||0), 0);
  const fmt = n => Number(n||0).toLocaleString();

  const cell = { padding:"11px 12px", fontSize:13, color:"#cbd5e1" };
  const head = { padding:"10px 12px", fontSize:11, fontWeight:700, color:"#94a3b8", textAlign:"right", background:"rgba(255,255,255,0.03)", borderBottom:"1px solid rgba(255,255,255,0.07)" };

  return(
    <div style={{padding:"28px",maxWidth:1280,margin:"0 auto"}}>
      <div style={{marginBottom:18}}>
        <h2 style={{fontSize:22,fontWeight:800,color:"#c8a03c",margin:0}}>{T.archiveTitle}</h2>
        <p style={{fontSize:13,color:"#475569",margin:"5px 0 0"}}>{T.archiveSubtitle}</p>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:18}}>
        <div style={{background:"rgba(200,160,60,0.08)",border:"1px solid rgba(200,160,60,0.25)",borderRadius:12,padding:"14px 18px"}}>
          <div style={{fontSize:11,color:"#94a3b8",marginBottom:4}}>{T.totalInv}</div>
          <div style={{fontSize:22,fontWeight:800,color:"#c8a03c"}}>{filtered.length}</div>
        </div>
        <div style={{background:"rgba(34,197,94,0.06)",border:"1px solid rgba(34,197,94,0.22)",borderRadius:12,padding:"14px 18px"}}>
          <div style={{fontSize:11,color:"#94a3b8",marginBottom:4}}>{T.totalVal}</div>
          <div style={{fontSize:22,fontWeight:800,color:"#22c55e"}}>{fmt(totalAll)} ر.س</div>
        </div>
        <div style={{background:"rgba(34,197,94,0.08)",border:"1px solid rgba(34,197,94,0.3)",borderRadius:12,padding:"14px 18px"}}>
          <div style={{fontSize:11,color:"#94a3b8",marginBottom:4}}>إجمالي المحصّل</div>
          <div style={{fontSize:22,fontWeight:800,color:"#22c55e"}}>{fmt(filtered.filter(i=>i.paidAt).reduce((s,i)=>s+(Number(i.rent)||0),0))} ر.س</div>
          <div style={{fontSize:10,color:"#475569",marginTop:2}}>{filtered.filter(i=>i.paidAt).length} فاتورة مدفوعة</div>
        </div>
        <div style={{background:"rgba(56,189,248,0.06)",border:"1px solid rgba(56,189,248,0.22)",borderRadius:12,padding:"14px 18px"}}>
          <div style={{fontSize:11,color:"#94a3b8",marginBottom:4}}>{T.lastInv}</div>
          <div style={{fontSize:18,fontWeight:800,color:"#38bdf8"}}>{sorted[0]?.invoiceNo || "—"}</div>
        </div>
      </div>

      <div style={{display:"flex",gap:10,marginBottom:14}}>
        <input
          value={search}
          onChange={e=>setSearch(e.target.value)}
          placeholder={T.searchPh}
          style={{flex:1,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,padding:"11px 14px",color:"#e2e8f0",fontSize:14,fontFamily:"'Tajawal',sans-serif",direction:"rtl",textAlign:"right",outline:"none"}}
        />
        <button onClick={refreshSignatures} title={T.refresh}
          style={{padding:"10px 16px",background:"rgba(56,189,248,0.1)",border:"1px solid rgba(56,189,248,0.3)",color:"#38bdf8",borderRadius:10,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif",whiteSpace:"nowrap"}}>
          {T.refresh}
        </button>
      </div>

      {filtered.length === 0 ? (
        <div style={{textAlign:"center",padding:"60px 24px",color:"#475569"}}>
          <div style={{fontSize:48,marginBottom:12}}>📭</div>
          <div style={{fontSize:15}}>لا توجد فواتير {q?`مطابقة لـ "${search}"`:"بعد"}</div>
          {!q && <div style={{fontSize:12,marginTop:6}}>أنشئ فاتورة من تبويب "تسجيل عميل"</div>}
        </div>
      ) : (
        <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,overflow:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",direction:"rtl",minWidth:900}}>
            <thead>
              <tr>
                <th style={head}>{T.colNo}</th>
                <th style={head}>{T.colDate}</th>
                <th style={head}>{T.colCustomer}</th>
                <th style={head}>العنوان</th>
                <th style={head}>{T.colRent}</th>
                <th style={head}>{T.colDeposit}</th>
                <th style={{...head,textAlign:"right"}}>{T.colTotal}</th>
                <th style={head}>{T.colPayment}</th>
                <th style={head}>{T.colPeriod}</th>
                <th style={head}>{T.colSig}</th>
                <th style={head}>حالة الدفع</th>
                <th style={head}>{T.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(i=>{
                const sig = signatures[i.invoiceNo];
                return (
                <tr key={i.id} style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                  <td style={{...cell,color:"#c8a03c",fontWeight:700,fontFamily:"monospace"}}>{i.invoiceNo}</td>
                  <td style={{...cell,direction:"ltr",textAlign:"right"}}>{i.date}</td>
                  <td style={{...cell,fontWeight:700,color:"#e2e8f0"}}>{i.customerName}</td>
                  <td style={cell}>
                    <div style={{fontWeight:700,color:"#e2e8f0"}}>وحدة {i.unit}</div>
                    <div style={{fontSize:11,color:"#94a3b8",marginTop:2}}>{i.district}{i.city ? ` · ${i.city}` : ' · الرياض'}</div>
                  </td>
                  <td style={{...cell,direction:"ltr",textAlign:"right"}}>{fmt(i.rent)}</td>
                  <td style={{...cell,direction:"ltr",textAlign:"right"}}>{fmt(i.deposit)}</td>
                  <td style={{...cell,direction:"ltr",textAlign:"right",color:"#22c55e",fontWeight:700}}>{fmt(i.total)}</td>
                  <td style={cell}>{i.paymentMethod}</td>
                  <td style={{...cell,fontSize:11,direction:"ltr",textAlign:"right"}}>
                    {i.contractStart} → {i.contractEnd}
                    {i.vacatedAt && (
                      <div style={{marginTop:3,fontSize:10,color:"#f59e0b",fontWeight:700,direction:"ltr",textAlign:"right"}}>
                        🚪 إخلاء مبكر: {i.vacatedAt}
                      </div>
                    )}
                  </td>
                  <td style={cell}>
                    {sig ? (
                      <button onClick={()=>setViewSig({...sig, invoiceNo:i.invoiceNo})}
                        style={{padding:"4px 10px",background:"rgba(34,197,94,0.15)",border:"1px solid rgba(34,197,94,0.4)",color:"#22c55e",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
                        {T.signed}
                      </button>
                    ) : (
                      <span style={{color:"#f59e0b",fontSize:11,fontWeight:700}}>{T.awaiting}</span>
                    )}
                  </td>
                  <td style={cell}>
                    {i.paidAt
                      ? <div style={{display:"flex",flexDirection:"column",gap:4}}>
                          <span style={{fontSize:12,fontWeight:800,color:"#22c55e"}}>✅ مدفوعة</span>
                          <span style={{fontSize:10,color:"#475569",direction:"ltr"}}>{i.paidAt}</span>
                          <button onClick={()=>unmarkInvoicePaid(i)}
                            style={{padding:"2px 7px",background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.3)",color:"#f87171",borderRadius:5,fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif",whiteSpace:"nowrap",marginTop:2}}>
                            ↩️ إلغاء الدفع
                          </button>
                        </div>
                      : <div style={{display:"flex",flexDirection:"column",gap:5}}>
                          <button onClick={()=>markInvoicePaid(i)}
                            style={{padding:"4px 10px",background:"rgba(34,197,94,0.1)",border:"1px solid rgba(34,197,94,0.35)",color:"#22c55e",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif",whiteSpace:"nowrap"}}>
                            💰 تسجيل دفع
                          </button>
                          {/* Combined: mark paid + open WhatsApp with link to the customer's final paid receipt page */}
                          {sig && (
                            <button onClick={async ()=>{
                              markInvoicePaid(i);
                              // Use the sign URL stored on the invoice (set at create time).
                              // If missing (older invoices), regenerate a token-based URL on the fly.
                              let signUrl = i.signUrl;
                              if(!signUrl){
                                try {
                                  signUrl = await buildTokenSignUrl({ ...i, fullName: i.customerName, phone: i.customerPhone }, sbConfig);
                                } catch(e){ console.warn('fallback sign URL build failed', e); }
                              }
                              if(!signUrl){ alert('تعذر بناء رابط النسخة الموقعة — حمّل العقد يدوياً.'); return; }
                              const msg = buildPaidReceiptMessage(i.customerName||'', i.invoiceNo, signUrl);
                              window.open(buildWaUrl(i.customerPhone, msg), '_blank');
                            }}
                              title="تسجيل الدفع وإرسال النسخة النهائية الموقعة للعميل عبر واتساب"
                              style={{padding:"4px 10px",background:"rgba(37,211,102,0.12)",border:"1px solid rgba(37,211,102,0.4)",color:"#25d366",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif",whiteSpace:"nowrap"}}>
                              💰 دفع + إرسال
                            </button>
                          )}
                        </div>
                    }
                  </td>
                  <td style={cell}>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      {!sig && (
                        <button onClick={()=>resendLink(i)} title="إعادة إرسال رابط التوقيع عبر واتساب"
                          style={{padding:"4px 9px",background:"rgba(37,211,102,0.12)",border:"1px solid rgba(37,211,102,0.4)",color:"#25d366",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
                          {T.resend}
                        </button>
                      )}
                      <button onClick={()=>openEdit(i)} title="تعديل بيانات الفاتورة"
                        style={{padding:"4px 9px",background:"rgba(200,160,60,0.12)",border:"1px solid rgba(200,160,60,0.4)",color:"#c8a03c",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
                        {T.editBtn}
                      </button>
                      {i.vacatedAt
                        ? <button onClick={()=>unmarkVacated(i)} title="إلغاء تسجيل الإخلاء المبكر"
                            style={{padding:"4px 9px",background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.4)",color:"#f59e0b",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
                            ↩️ إخلاء
                          </button>
                        : <button onClick={()=>markVacated(i)} title="تسجيل إخلاء مبكر للوحدة (لا يغير العقد الموقع)"
                            style={{padding:"4px 9px",background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.35)",color:"#f59e0b",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
                            🚪 إخلاء
                          </button>
                      }
                      <button onClick={()=>openDeleteModal(i)} title="حذف الفاتورة"
                        style={{padding:"4px 9px",background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.35)",color:"#ef4444",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
                        🗑
                      </button>
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}

      {/* Signature view modal */}
      {/* ── PIN delete modal ── */}
      {deleteTarget && (
        <div style={{position:"fixed",inset:0,zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,0.85)",backdropFilter:"blur(8px)"}}>
          <div dir="rtl" style={{background:"#181b27",border:"1px solid rgba(200,160,60,0.3)",borderRadius:20,padding:32,width:"min(360px,92vw)",textAlign:"center",fontFamily:"'Tajawal',sans-serif"}}>
            <div style={{fontSize:36,marginBottom:10}}>🗑</div>
            <div style={{fontSize:17,fontWeight:800,color:"#ef4444",marginBottom:4}}>حذف فاتورة</div>
            <div style={{fontSize:12,color:"#94a3b8",marginBottom:6}}>
              <span style={{color:"#c8a03c",fontWeight:700,fontFamily:"monospace"}}>{deleteTarget.invoiceNo}</span>
              {" · "}{deleteTarget.customerName}
            </div>
            <div style={{fontSize:12,color:"#475569",marginBottom:20}}>أدخل الرمز السري لتأكيد الحذف</div>

            {/* dots */}
            <div style={{display:"flex",gap:12,justifyContent:"center",marginBottom:20}}>
              {[0,1,2,3].map(i=>(
                <div key={i} style={{width:14,height:14,borderRadius:"50%",
                  background:delPin.length>i?"#ef4444":"rgba(255,255,255,0.1)",
                  border:"2px solid",borderColor:delPin.length>i?"#ef4444":"rgba(255,255,255,0.15)",
                  transition:"background .15s"}}/>
              ))}
            </div>

            {delPinLocked && (
              <div style={{fontSize:12,color:"#ef4444",marginBottom:14,fontWeight:700,background:"rgba(239,68,68,0.08)",padding:"10px 14px",borderRadius:10,border:"1px solid rgba(239,68,68,0.2)"}}>
                🔒 محظور — حاول بعد {Math.max(1, Math.ceil((delLockUntil - Date.now()) / 60000))} دقيقة
              </div>
            )}

            {/* numpad */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:16}}>
              {[1,2,3,4,5,6,7,8,9,"","0","⌫"].map((n,i)=>(
                <button key={i} onClick={()=>{
                  if(delPinLocked) return;
                  if(n==="⌫"){ setDelPin(p=>p.slice(0,-1)); setDelPinError(false); }
                  else if(n!==""){
                    const next = delPin + n;
                    setDelPin(next); setDelPinError(false);
                    if(next.length===4) setTimeout(()=>submitDeletePin(next), 150);
                  }
                }} disabled={n===""||delPinLocked}
                  style={{padding:"14px",borderRadius:12,border:"1px solid",cursor:n===""||delPinLocked?"default":"pointer",
                    fontSize:18,fontWeight:700,fontFamily:"'Tajawal',sans-serif",
                    background:n===""||delPinLocked?"transparent":delPinError?"rgba(239,68,68,0.1)":"rgba(255,255,255,0.05)",
                    color:n===""||delPinLocked?"transparent":delPinError?"#ef4444":"#e2e8f0",
                    borderColor:n===""||delPinLocked?"transparent":"rgba(255,255,255,0.1)",
                    opacity:delPinLocked?0.3:1,transition:"background .1s"}}>
                  {n}
                </button>
              ))}
            </div>

            {delPinError && !delPinLocked && (
              <div style={{fontSize:12,color:"#ef4444",marginBottom:12,fontWeight:700}}>
                رمز خاطئ{delPinAttempts>=3&&delPinAttempts<5?` · ${5-delPinAttempts} محاولات متبقية`:""}
              </div>
            )}

            <button onClick={closeDeleteModal}
              style={{width:"100%",padding:"10px",borderRadius:10,background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",color:"#64748b",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
              إلغاء
            </button>
          </div>
        </div>
      )}

      {viewSig && (
        <div onClick={()=>setViewSig(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:24}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:14,padding:"28px 32px",maxWidth:560,width:"100%",border:"1px solid #e8e0cc"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18,paddingBottom:12,borderBottom:"1px solid #e8e0cc"}}>
              <div>
                <div style={{fontSize:11,color:"#79858d",marginBottom:2}}>توقيع العميل · فاتورة</div>
                <div style={{fontSize:18,color:"#c8a03c",fontWeight:800,fontFamily:"monospace"}}>{viewSig.invoiceNo}</div>
              </div>
              <button onClick={()=>setViewSig(null)} style={{background:"none",border:"none",fontSize:24,color:"#79858d",cursor:"pointer"}}>✕</button>
            </div>
            <div style={{textAlign:"center",marginBottom:14}}>
              <img src={viewSig.dataUrl} alt="signature" style={{maxWidth:"100%",height:"auto",border:"1px solid #e8e0cc",borderRadius:8,background:"#faf8f3"}}/>
            </div>
            <div style={{fontSize:13,color:"#202a44",lineHeight:1.7}}>
              <div><b>العميل:</b> {viewSig.customerName}</div>
              <div><b>الوحدة:</b> {viewSig.unit}</div>
              <div style={{direction:"ltr",textAlign:"right"}}><b>تاريخ التوقيع:</b> {String(viewSig.signedAt).slice(0,10)}</div>
            </div>
            <div style={{display:"flex",gap:10,marginTop:14,flexWrap:"wrap"}}>
              <a href={viewSig.dataUrl} download={`signature-${viewSig.invoiceNo}.png`}
                style={{display:"inline-block",padding:"9px 18px",background:"rgba(172,144,85,0.15)",color:"#ac9055",border:"1px solid #ac9055",borderRadius:8,fontSize:13,fontWeight:700,textDecoration:"none",fontFamily:"'Tajawal',sans-serif"}}>
                🖊 صورة التوقيع
              </a>
              <button onClick={()=>{
                // Open the signed contract in a new window and trigger the browser's
                // print dialog — user picks "Save as PDF" to get a real PDF file.
                // Same pattern as the new-invoice download flow.
                const inv = (invoices||[]).find(i=>i.invoiceNo===viewSig.invoiceNo) || {};
                const html = buildSignedPdfHtml({...inv, fullName: inv.customerName||viewSig.customerName}, viewSig.dataUrl);
                const blob = new Blob([html],{type:'text/html;charset=utf-8'});
                const url = URL.createObjectURL(blob);
                const win = window.open(url,'_blank');
                if(win) setTimeout(()=>{ win.print(); }, 800);
              }} style={{padding:"9px 18px",background:"#ac9055",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
                📄 تحميل العقد الموقّع (PDF)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit invoice modal */}
      {editing && (
        <div onClick={()=>setEditing(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:24,overflow:"auto"}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"#0d0f1a",borderRadius:14,padding:"24px 28px",maxWidth:680,width:"100%",border:"1px solid rgba(200,160,60,0.4)",fontFamily:"'Tajawal',sans-serif",direction:"rtl"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18,paddingBottom:12,borderBottom:"1px solid rgba(255,255,255,0.07)"}}>
              <div>
                <div style={{fontSize:11,color:"#94a3b8",marginBottom:2}}>تعديل فاتورة</div>
                <div style={{fontSize:18,color:"#c8a03c",fontWeight:800,fontFamily:"monospace"}}>{editing.invoiceNo}</div>
              </div>
              <button onClick={()=>setEditing(null)} style={{background:"none",border:"none",fontSize:24,color:"#94a3b8",cursor:"pointer"}}>✕</button>
            </div>
            {(() => {
              const lbl = { display:"block",fontSize:11,color:"#94a3b8",marginBottom:5 };
              const inp = { width:"100%",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,padding:"9px 12px",color:"#e2e8f0",fontSize:13,fontFamily:"'Tajawal',sans-serif",direction:"rtl",textAlign:"right",outline:"none",boxSizing:"border-box" };
              const ltr = { ...inp, direction:"ltr",textAlign:"left" };
              const F = (k,v) => setEditForm(p=>({...p,[k]:v}));
              return (
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                  <div style={{gridColumn:"1 / -1"}}>
                    <label style={lbl}>اسم العميل</label>
                    <input value={editForm.customerName} onChange={e=>F("customerName",e.target.value)} style={inp}/>
                  </div>
                  <div>
                    <label style={lbl}>رقم الجوال</label>
                    <input value={editForm.customerPhone} onChange={e=>F("customerPhone",e.target.value)} style={ltr}/>
                  </div>
                  <div>
                    <label style={lbl}>رقم الهوية</label>
                    <input value={editForm.idNumber} onChange={e=>F("idNumber",e.target.value)} style={ltr}/>
                  </div>
                  <div>
                    <label style={lbl}>رقم الوحدة</label>
                    <input value={editForm.unit} onChange={e=>F("unit",e.target.value)} style={inp}/>
                  </div>
                  <div>
                    <label style={lbl}>الحي</label>
                    <input value={editForm.district} onChange={e=>F("district",e.target.value)} style={inp}/>
                  </div>
                  <div>
                    <label style={lbl}>المدينة</label>
                    <input value={editForm.city} onChange={e=>F("city",e.target.value)} style={inp}/>
                  </div>
                  <div>
                    <label style={lbl}>الإيجار الشهري</label>
                    <input type="number" value={editForm.rent} onChange={e=>F("rent",e.target.value)} style={ltr}/>
                  </div>
                  <div>
                    <label style={lbl}>مبلغ التأمين</label>
                    <input type="number" value={editForm.deposit} onChange={e=>F("deposit",e.target.value)} style={ltr}/>
                  </div>
                  <div>
                    <label style={lbl}>بداية العقد</label>
                    <input type="date" value={editForm.contractStart} onChange={e=>F("contractStart",e.target.value)} style={ltr}/>
                  </div>
                  <div>
                    <label style={lbl}>نهاية العقد</label>
                    <input type="date" value={editForm.contractEnd} onChange={e=>F("contractEnd",e.target.value)} style={ltr}/>
                  </div>
                  <div>
                    <label style={lbl}>آلية الدفع</label>
                    <select value={editForm.paymentMethod} onChange={e=>F("paymentMethod",e.target.value)} style={inp}>
                      <option value="">— اختر —</option>
                      <option value="كاش">كاش</option>
                      <option value="تحويل">تحويل بنكي</option>
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>غرامة التأخير اليومية</label>
                    <input type="number" value={editForm.lateDailyRate} onChange={e=>F("lateDailyRate",e.target.value)} style={ltr}/>
                  </div>
                </div>
              );
            })()}
            <div style={{display:"flex",gap:10,marginTop:20,justifyContent:"flex-start"}}>
              <button onClick={saveEdit}
                style={{padding:"10px 22px",background:"linear-gradient(135deg,#c8a03c,#9a6f10)",border:"none",color:"#0d0f1a",borderRadius:8,fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
                💾 حفظ التغييرات
              </button>
              <button onClick={()=>setEditing(null)}
                style={{padding:"10px 18px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",color:"#94a3b8",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
                إلغاء
              </button>
            </div>
            {!signatures[editing.invoiceNo] && (
              <div style={{marginTop:14,padding:"8px 12px",background:"rgba(56,189,248,0.06)",border:"1px solid rgba(56,189,248,0.18)",borderRadius:8,fontSize:11,color:"#7dd3fc",lineHeight:1.6}}>
                💡 بعد التعديل، اضغط <b>"📲 إعادة إرسال"</b> من جدول الأرشيف لإرسال الرابط المحدّث للعميل.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
